import type { ClientGrapevineOnboardingStatus } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useGrapevineAppApi } from '@/features/api/hooks/grapevine-app/useGrapevineAppApi';
import { useAuth } from '@/features/authorization/provider/useAuth';
import { BadgeKey, useBadgeStore } from '@/shared/store/useBadgeStore';

export const useGetGrapevineOnboardingStatus = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const api = useGrapevineAppApi();

  const updateBadge = useBadgeStore((state) => state.updateBadge);

  const handleSelect = useCallback(
    (status: ClientGrapevineOnboardingStatus) => {
      queryClient.setQueryData([API_CACHE_KEYS.OWN_CHATS], status.ownChats);

      const unreadMessageCount = status.ownChats?.reduce(
        (sum, chat) => sum + (chat.unreadMessageCount ?? 0),
        0
      );
      updateBadge(
        BadgeKey.Chat,
        unreadMessageCount === 0 ? undefined : unreadMessageCount
      );
      queryClient.setQueryData(
        [API_CACHE_KEYS.SUGGESTION_CATEGORIES],
        status.suggestionCategories
      );

      queryClient.setQueryData(
        [API_CACHE_KEYS.CONVENIENCES],
        status.conveniences
      );
    },
    [queryClient, updateBadge]
  );

  return useQuery({
    queryKey: [API_CACHE_KEYS.GRAPEVINE_ONBOARDING_STATUS],
    queryFn: () => api.getOnboardingStatus(),
    enabled: isAuthenticated,
    select: handleSelect
  });
};
